import Cookies from "js-cookie";

/** This is only ever set in dev mode to override UTM cookie domain. */
declare global {
  interface Window {
    stotlesCookieDomain?: string;
  }
}

export const STOTLES_UTM_COOKIE = "stotles_utm";

export type VisitInfo = {
  referrer: string;
  url: string;
  utmParams: UtmParams;
  /** Last Stotles page URL before sign up / log in; updated on every page view until auth. */
  last_page_url_before_form_submit?: string;
};

type UtmParams = {
  source?: string;
  medium?: string;
  campaign?: string;
  content?: string;
  term?: string;
  [key: string]: string | undefined;
};

function hasAnalyticsConsent(): boolean {
  const consent = Cookies.get("cookieyes-consent");
  if (!consent) {
    return false;
  }
  const pairs = consent.split(",");
  return pairs.some((pair) => {
    const [key, value] = pair.split(":");
    return key === "analytics" && value === "yes";
  });
}

function captureVisitInfo(): VisitInfo {
  const params = new URLSearchParams(window.location.search);
  const visitInfo: VisitInfo = {
    referrer: document.referrer,
    url: location.href,
    utmParams: {},
    ...(shouldSkipLastPageUrlUpdate() ? {} : { last_page_url_before_form_submit: location.href }),
  };

  params.forEach((v, k) => {
    const match = k.match(/utm_(.+)/);
    if (!match || !match[1]) {
      return;
    }
    visitInfo.utmParams[match[1]] = v;
  });

  return visitInfo;
}

function persistVisitInfo(visitInfo: VisitInfo): void {
  if (Cookies.get(STOTLES_UTM_COOKIE)) {
    return;
  }

  Cookies.set(STOTLES_UTM_COOKIE, JSON.stringify(visitInfo), {
    domain: window.stotlesCookieDomain || "stotles.com",
  });
}

const APP_HOSTNAME = "app.stotles.com";

/** True if we should not set or update last_page_url_before_form_submit (app domain). */
function shouldSkipLastPageUrlUpdate(): boolean {
  if (typeof window === "undefined") return true;
  if (window.location.hostname === APP_HOSTNAME) return true;
  return false;
}

/**
 * Updates the stotles_utm cookie's last_page_url_before_form_submit to the current page.
 * Call on every Stotles page load so we record the last page visited before sign up / log in.
 * Does not update when the user is on the signup or login page.
 */
export function updateLastPageUrlBeforeFormSubmit(): void {
  if (shouldSkipLastPageUrlUpdate()) {
    return;
  }
  const existing = Cookies.get(STOTLES_UTM_COOKIE);
  if (!existing) {
    return;
  }
  try {
    const visitInfo = JSON.parse(existing) as VisitInfo;
    const urlToStore = location.href;
    visitInfo.last_page_url_before_form_submit = urlToStore;
    Cookies.set(STOTLES_UTM_COOKIE, JSON.stringify(visitInfo), {
      domain: window.stotlesCookieDomain || "stotles.com",
    });
  } catch {
    // ignore invalid cookie
  }
}

let consentListenerRegistered = false;

export function storeUtmParams(): void {
  if (Cookies.get(STOTLES_UTM_COOKIE)) {
    return;
  }

  // Capture visit info eagerly so UTM params aren't lost if the user navigates before consenting
  const visitInfo = captureVisitInfo();

  if (hasAnalyticsConsent()) {
    persistVisitInfo(visitInfo);
    return;
  }

  if (consentListenerRegistered) {
    return;
  }
  consentListenerRegistered = true;

  // Listen for consent to be granted later (e.g. user accepts the banner)
  const onConsentUpdate = (event: Event) => {
    const detail = (event as CustomEvent).detail;
    if (detail?.accepted?.includes("analytics")) {
      persistVisitInfo(visitInfo);
      document.removeEventListener("cookieyes_consent_update", onConsentUpdate);
    }
  };
  document.addEventListener("cookieyes_consent_update", onConsentUpdate);
}
